// Compliance standards × hubCMD features mapping.
// Clause/section references are best-effort, drawn from the published standards.
// Always validate against the controlling document for your audit.

export const STANDARDS = [
  { key: "iso9001",     name: "ISO 9001:2015",            short: "Quality Management" },
  { key: "iso14001",    name: "ISO 14001:2015",           short: "Environmental" },
  { key: "iso45001",    name: "ISO 45001:2018",           short: "Occupational H&S" },
  { key: "iso13485",    name: "ISO 13485:2016",           short: "Medical Devices QMS" },
  { key: "iso17025",    name: "ISO/IEC 17025:2017",       short: "Testing & Calibration Labs" },
  { key: "iso50001",    name: "ISO 50001:2018",           short: "Energy Management" },
  { key: "iso27001",    name: "ISO/IEC 27001:2022",       short: "Information Security" },
  { key: "iso55001",    name: "ISO 55001:2014",           short: "Asset Management" },
  { key: "iso22301",    name: "ISO 22301:2019",           short: "Business Continuity" },
  { key: "iso37001",    name: "ISO 37001:2016",           short: "Anti-Bribery" },
  { key: "iso22716",    name: "ISO 22716:2007",           short: "Cosmetics GMP" },
  { key: "iso3834",     name: "ISO 3834-2:2021",          short: "Welding Quality" },
  { key: "iatf16949",   name: "IATF 16949:2016",          short: "Automotive QMS" },
  { key: "as9100",      name: "AS9100D:2016",             short: "Aerospace QMS" },
  { key: "haccp",       name: "HACCP (Codex CAC/RCP 1)",  short: "Food Safety" },
  { key: "sqf",         name: "SQF Edition 9",            short: "GFSI · Food Safety" },
  { key: "brcgs",       name: "BRCGS Food Issue 9",       short: "GFSI · Food Safety" },
  { key: "fssc22000",   name: "FSSC 22000 v5.1",          short: "GFSI · Food Safety Mgmt" },
  { key: "nadcap",      name: "NADCAP",                   short: "Aerospace Special Processes" },
  { key: "sa8000",      name: "SA8000:2014",              short: "Social Accountability" },
  { key: "fsc",         name: "FSC-STD-40-004 v3-1",      short: "Forestry Chain of Custody" },
  { key: "fda_cgmp",    name: "FDA cGMP (21 CFR 210/211/117)", short: "US Drug & Food GMP" },
  { key: "fsma",        name: "FSMA · 21 CFR 117",        short: "US Food Safety Modernization" },
  { key: "mdsap",       name: "MDSAP",                    short: "Medical Device Single Audit" },
  { key: "cfr_part_11", name: "FDA 21 CFR Part 11",       short: "Electronic Records & Signatures" },
];

const C = (std, ...clauses) => ({ std, clauses });

export const FEATURE_GROUPS = [
  {
    group: "Records & Signatures",
    features: [
      {
        id: "esign",
        title: "Electronic signatures · auto-captured on every change",
        summary: "Every create / update / comment / status-change / 5-Whys / effectiveness verdict records the user's typed name + UTC timestamp; the activity log is append-only and immutable.",
        citations: [
          C("cfr_part_11", "§11.10(a) validation", "§11.10(b) records", "§11.10(d) access", "§11.50 signed records", "§11.70 signature-record linking"),
          C("iso9001", "7.5.3 control of documented information"),
          C("iso13485", "4.2.5 control of records"),
          C("iso17025", "7.5 technical records", "8.4 control of records"),
          C("iso27001", "A.5.33 protection of records", "A.8.15 logging"),
          C("iatf16949", "7.5.3.2 records retention"),
          C("as9100", "7.5.3 documented information"),
          C("sqf", "2.2.1 document control", "2.2.2 records"),
          C("brcgs", "3.3 record completion & maintenance"),
          C("fssc22000", "ISO 22000:2018 §7.5 documented information"),
          C("fda_cgmp", "§211.180 records & reports", "§211.188 batch production records"),
          C("fsma", "§117.330 records subject to inspection"),
          C("haccp", "Principle 7 record-keeping"),
          C("mdsap", "Records integrity across jurisdictions"),
          C("iso14001", "7.5.3"),
          C("iso45001", "7.5.3"),
          C("iso50001", "7.5.3"),
          C("iso22301", "7.5.3"),
          C("iso37001", "7.5.3"),
          C("iso55001", "7.5.3"),
          C("iso22716", "§17 documentation"),
          C("iso3834", "§14 records"),
          C("nadcap", "AC7000 §3.13 records"),
          C("sa8000", "9.1 records of conformity"),
          C("fsc", "1.4 documented procedures"),
        ],
      },
      {
        id: "trail",
        title: "Immutable activity audit-trail",
        summary: "Append-only log of every state change, with actor + role + timestamp. Records preserved when users are deactivated (signatures keep the original name as immutable text).",
        citations: [
          C("cfr_part_11", "§11.10(e) audit trails"),
          C("iso9001", "7.5.3.2 retention & disposition"),
          C("iso13485", "4.2.5"),
          C("iso17025", "8.4"),
          C("iso27001", "A.8.15 logging", "A.8.34 protection of info during audit testing"),
          C("iatf16949", "7.5.3.2.1 record retention"),
          C("as9100", "7.5.3 retention"),
          C("sqf", "2.2.2 records retention"),
          C("brcgs", "3.3 record retention"),
          C("fssc22000", "ISO 22000 §7.5.3 retention"),
          C("fda_cgmp", "§211.180(c) retention ≥1y post-expiry"),
          C("fsma", "§117.315 record retention ≥2y"),
          C("haccp", "Principle 7 retention"),
          C("mdsap", "Records integrity"),
        ],
      },
      {
        id: "pdf",
        title: "Tamper-evident signed PDFs (SHA-256 integrity hash)",
        summary: "Meeting transcripts and audit close-out reports generate a SHA-256 hashed PDF with the integrity fingerprint embedded in the footer + stored on the record.",
        citations: [
          C("cfr_part_11", "§11.10(c) records protection", "§11.50 signed records"),
          C("iso9001", "7.5.3"),
          C("iso13485", "4.2.5 records integrity"),
          C("iso17025", "7.11 control of data"),
          C("iso27001", "A.8.24 cryptography", "A.5.33 protection of records"),
          C("iatf16949", "7.5.3.2 records"),
          C("as9100", "7.5.3"),
          C("sqf", "2.2.2"),
          C("brcgs", "3.3"),
          C("fssc22000", "ISO 22000 7.5.3"),
          C("fda_cgmp", "§211.68 automatic equipment & records"),
          C("fsma", "§117.330 record integrity"),
          C("mdsap", "Records integrity"),
        ],
      },
    ],
  },
  {
    group: "Risk, Hazard & Root Cause",
    features: [
      {
        id: "risk_register",
        title: "Risk Register (Likelihood × Severity, 1–25)",
        summary: "Every action carries an L × S risk score with bands Low / Medium / High / Critical. Dashboard rolls up the weighted profile; high-risk items require root-cause analysis before close-out.",
        citations: [
          C("iso9001", "6.1 actions to address risks & opportunities"),
          C("iso14001", "6.1.2 environmental aspects", "6.1.3 compliance obligations"),
          C("iso45001", "6.1.2 hazard identification & risk assessment"),
          C("iso13485", "7.1 planning of product realization (risk-based)"),
          C("iso27001", "6.1.2 information security risk assessment", "6.1.3 treatment"),
          C("iso22301", "8.2 business impact analysis & risk assessment"),
          C("iso50001", "6.1 risks & opportunities"),
          C("iso37001", "4.5 anti-bribery risk assessment"),
          C("iso22716", "§14 risk-based GMP controls"),
          C("iatf16949", "6.1.2.3 contingency plans"),
          C("as9100", "8.1.1 operational risk management"),
          C("haccp", "Principle 1 hazard analysis"),
          C("sqf", "2.4.2 food safety risk assessment"),
          C("brcgs", "1.1.2 risk management commitment", "2 food safety plan"),
          C("fssc22000", "ISO 22000 §6.1 risks/opportunities", "§8.5 hazard control"),
          C("fda_cgmp", "§117 Subpart C preventive controls"),
          C("fsma", "§117.130 hazard analysis", "§117.135 preventive controls"),
          C("iso55001", "6.1 risks & opportunities"),
          C("mdsap", "Design / process risk per ISO 14971"),
        ],
      },
      {
        id: "five_whys",
        title: "5-Whys root cause · required on High/Critical risk",
        summary: "Actions with risk score ≥12 cannot be closed without a signed 5-Whys chain documenting the root cause.",
        citations: [
          C("iso9001", "10.2.1 nonconformity & corrective action"),
          C("iatf16949", "10.2.3 problem solving", "10.2.6 customer complaints"),
          C("as9100", "10.2.1 corrective action with root-cause requirement"),
          C("iso13485", "8.5.2 corrective action"),
          C("iso17025", "8.7 corrective action"),
          C("iso27001", "10.1 nonconformity & corrective action"),
          C("iso14001", "10.2"),
          C("iso45001", "10.2"),
          C("iso50001", "10.1"),
          C("iso22301", "10.1"),
          C("iso37001", "10.2"),
          C("iso55001", "10.1"),
          C("sqf", "2.5.4 corrective & preventive action"),
          C("brcgs", "3.7 corrective & preventive actions"),
          C("fssc22000", "ISO 22000 §8.9 NC, correction & corrective actions"),
          C("haccp", "Principle 5 corrective actions"),
          C("fda_cgmp", "§211.192 production record review", "§820.100 CAPA (devices)"),
          C("fsma", "§117.150 corrective actions & corrections"),
          C("nadcap", "AC7004 root-cause analysis"),
          C("mdsap", "CAPA — all jurisdictions"),
        ],
      },
      {
        id: "effectiveness",
        title: "30-day effectiveness review (signed proof + photo)",
        summary: "Auto-scheduled 30 days after close-out. Verifier must supply a comment, at least one proof attachment, and a signed verdict. Not-verified re-opens the action.",
        citations: [
          C("iso9001", "10.2.1(d-f) review effectiveness of corrective actions"),
          C("iatf16949", "10.2.4 problem solving · effectiveness"),
          C("as9100", "10.2.1(f) verify effectiveness"),
          C("iso13485", "8.5.2 effectiveness review"),
          C("iso17025", "8.7.3 effectiveness review"),
          C("iso27001", "10.1(b) determine effectiveness"),
          C("iso14001", "10.2(e)"),
          C("iso45001", "10.2(c)"),
          C("sqf", "2.5.4.2 verification of CAPA"),
          C("brcgs", "3.7.2 root-cause effectiveness verification"),
          C("fssc22000", "ISO 22000 §8.9.3 verification of corrective actions"),
          C("haccp", "Principle 6 verification"),
          C("fda_cgmp", "§820.100(a)(4) verify effectiveness (devices)"),
          C("fsma", "§117.150 verification & validation"),
          C("mdsap", "CAPA effectiveness"),
        ],
      },
    ],
  },
  {
    group: "Audits, Inspections & Photo Evidence",
    features: [
      {
        id: "checklists",
        title: "Audit & inspection checklists with photo evidence",
        summary: "Build standard-work templates once; run pass/fail/N-A audits with notes and photo attachments. Trend dashboards roll up pass-rate over time per location.",
        citations: [
          C("iso9001", "9.2 internal audit"),
          C("iso14001", "9.2"), C("iso45001", "9.2"),
          C("iso13485", "8.2.4 internal audit"),
          C("iso17025", "8.8 internal audits"),
          C("iso50001", "9.2"), C("iso27001", "9.2"),
          C("iso22301", "9.2"), C("iso37001", "9.2"),
          C("iso55001", "9.2"),
          C("iatf16949", "9.2.2 internal audit"),
          C("as9100", "9.2"),
          C("sqf", "2.5.7 internal audits"),
          C("brcgs", "3.4 internal audit"),
          C("fssc22000", "ISO 22000 §9.2"),
          C("haccp", "Principle 6 verification audits"),
          C("fda_cgmp", "§211.22 QC unit", "§820.22 quality audit (devices)"),
          C("fsma", "§117.165 verification activities"),
          C("nadcap", "AC7004 audit program"),
          C("sa8000", "9.10 internal social audits"),
          C("mdsap", "Internal audit"),
        ],
      },
      {
        id: "5s",
        title: "Daily 5S walks & Gemba audits",
        summary: "Mobile-lite UI for floor supervisors to run daily 5S / Gemba walks with required photos on every fail and auto-creation of corrective actions.",
        citations: [
          C("iso9001", "10.3 continual improvement"),
          C("iatf16949", "10.3.1 continual improvement"),
          C("as9100", "10.3"),
          C("iso50001", "10.3"),
          C("brcgs", "4.11 housekeeping & hygiene"),
          C("sqf", "11.2 cleaning & sanitation"),
        ],
      },
    ],
  },
  {
    group: "Traceability & Identification",
    features: [
      {
        id: "ids",
        title: "Human-readable traceability IDs (ACT-NNNN, CHK-NNNN, MTG-NNNN, WO-NNNN…)",
        summary: "Every action, audit run, meeting, work order, destination, and organization gets a sequential ID stamped on creation. IDs surface on every UI card and signed PDF for auditor cross-reference.",
        citations: [
          C("iso9001", "8.5.2 identification & traceability"),
          C("iso13485", "7.5.8 identification", "7.5.9 traceability"),
          C("iatf16949", "8.5.2 identification & traceability"),
          C("as9100", "8.5.2"),
          C("iso17025", "7.4.4 item identification"),
          C("iso22716", "§7 production traceability"),
          C("haccp", "Principle 7 traceability"),
          C("sqf", "2.6.1 product identification & trace", "2.6.3 traceability"),
          C("brcgs", "3.9 traceability"),
          C("fssc22000", "ISO 22000 §8.3 traceability"),
          C("fda_cgmp", "§211.130 packaging & labeling control"),
          C("fsma", "§204 traceability (FSMA 204)"),
          C("nadcap", "AC7000 §3.6 traceability"),
          C("fsc", "1.5 product identification", "5 record-keeping for CoC"),
          C("mdsap", "UDI / traceability"),
        ],
      },
      {
        id: "wo",
        title: "Maintenance work requests · on-the-fly with photo attachments",
        summary: "Spot a defect → snap a photo → create a WO-NNNN with the asset tagged. Supervisors get the request with the photo proof attached and a closed-loop action ID.",
        citations: [
          C("iso9001", "7.1.3 infrastructure", "8.5 production & service provision"),
          C("iso55001", "8.3 management of change", "7.5 information"),
          C("iso14001", "8.1 operational planning & control"),
          C("iso50001", "8.2 design (significant energy use)"),
          C("iatf16949", "8.5.1.1 control plan", "7.1.3.1 plant / facility / equipment planning"),
          C("as9100", "7.1.3 infrastructure"),
          C("brcgs", "4.7 maintenance"),
          C("sqf", "11.5.1 preventative maintenance"),
          C("fssc22000", "ISO/TS 22002-1 §11 maintenance"),
          C("fda_cgmp", "§211.67 equipment cleaning & maintenance", "§211.68 automatic equipment"),
          C("iso22716", "§5.3 equipment maintenance"),
        ],
      },
    ],
  },
  {
    group: "Management Review & Communication",
    features: [
      {
        id: "mgmt_review",
        title: "Meeting rooms · signed management-review transcripts",
        summary: "Schedule meetings or auto-generate a periodic management review from the trend report. Attendees sign in; on close-out, a SHA-256-hashed PDF transcript is generated and dispatched to chosen destinations.",
        citations: [
          C("iso9001", "9.3 management review"),
          C("iso14001", "9.3"), C("iso45001", "9.3"),
          C("iso13485", "5.6 management review"),
          C("iso17025", "8.9 management reviews"),
          C("iso50001", "9.3"), C("iso27001", "9.3"),
          C("iso22301", "9.3"), C("iso37001", "9.3"),
          C("iso55001", "9.3"),
          C("iatf16949", "9.3.1.1 mgmt review supplemental"),
          C("as9100", "9.3"),
          C("sqf", "2.1.3 management review"),
          C("brcgs", "1.1.4 management review"),
          C("fssc22000", "ISO 22000 §9.3"),
          C("haccp", "Management commitment & review"),
          C("fda_cgmp", "§211.180(e) annual product review", "§820.20 management responsibility"),
          C("nadcap", "AC7000 §3.10 management commitment"),
          C("sa8000", "9.4 management review"),
          C("mdsap", "Management review"),
        ],
      },
      {
        id: "dispatch",
        title: "Form distribution · email / MS Teams / SharePoint",
        summary: "Admins configure named destinations once. On meeting/audit close-out, the signed PDF is dispatched and the dispatch event is logged with timestamp + actor.",
        citations: [
          C("iso9001", "7.4 communication", "7.5.3 control of documented info"),
          C("iso14001", "7.4 communication"),
          C("iso45001", "7.4"),
          C("iso13485", "5.5.3 internal communication"),
          C("iso27001", "A.5.14 information transfer"),
          C("sqf", "2.2.1 document control"),
          C("brcgs", "3.2 document control"),
        ],
      },
    ],
  },
  {
    group: "People, Access & Competence",
    features: [
      {
        id: "rbac",
        title: "Role-based access (Controller / Admin / User) + soft-delete",
        summary: "Three-tier RBAC; only Controllers can issue seat tokens or change roles. Deactivation locks login + frees the seat but preserves every historical signature for the audit trail.",
        citations: [
          C("iso27001", "A.5.15 access control", "A.5.16 identity management", "A.5.18 access rights", "A.5.17 authentication info"),
          C("iso9001", "7.1.6 organizational knowledge", "7.2 competence"),
          C("iso13485", "6.2 human resources"),
          C("iso17025", "6.2 personnel"),
          C("iso37001", "7.2 competence", "7.3 awareness", "A.8 anti-bribery responsibilities"),
          C("cfr_part_11", "§11.10(d) limit access to authorized individuals", "§11.300 controls for identification codes / passwords"),
          C("iatf16949", "7.2.3 internal auditor competency"),
          C("as9100", "7.2 competence"),
          C("sa8000", "9.6 worker representative", "1-7 social accountability rights"),
          C("sqf", "2.9 training"),
          C("brcgs", "7 personnel training"),
          C("fssc22000", "ISO 22000 §7.2 competence"),
          C("fda_cgmp", "§211.25 personnel qualifications"),
          C("fsma", "§117.4 qualified individual"),
        ],
      },
      {
        id: "seat_billing",
        title: "Token-on-consumption billing · seat licensing",
        summary: "Tokens are free to generate; the company is billed only when a user consumes their token. Pricing snapshotted at creation + at consumption time → immutable billing-event history.",
        citations: [
          C("iso27001", "A.5.20 supplier agreements", "A.5.32 IP records"),
          C("iso9001", "8.4 control of externally provided products & services"),
          C("iso37001", "8.4 financial controls", "8.5 non-financial controls"),
          C("sa8000", "5 remuneration"),
        ],
      },
    ],
  },
  {
    group: "Lean & Continuous Improvement",
    features: [
      {
        id: "pdca",
        title: "PDCA cycle tracker · 8-Wastes tagging",
        summary: "Tag every action against the 8 waste categories; visualize Plan/Do/Check/Act rolling counts on the dashboard to drive continual improvement.",
        citations: [
          C("iso9001", "0.3.2 PDCA cycle", "10.3 continual improvement"),
          C("iso14001", "0.4 PDCA", "10.3"),
          C("iso45001", "0.5 PDCA", "10.3"),
          C("iso50001", "0.3 PDCA", "10.3"),
          C("iso27001", "10.2 continual improvement"),
          C("iso22301", "10.2"), C("iso37001", "10.3"),
          C("iatf16949", "10.3.1 continual improvement"),
          C("as9100", "10.3"),
          C("sqf", "2.5 verification"),
          C("brcgs", "1.1.5 continual improvement"),
          C("fssc22000", "ISO 22000 §10.3"),
        ],
      },
      {
        id: "trends",
        title: "Trend dashboards · automatic data analysis",
        summary: "Roll up pass-rate, waste tagging, risk weight, and corrective-action aging across the org; generate periodic reports tied to management review.",
        citations: [
          C("iso9001", "9.1.1 monitoring & measurement", "9.1.3 analysis & evaluation"),
          C("iso14001", "9.1.1"), C("iso45001", "9.1.1"),
          C("iso50001", "9.1 monitoring of EnPIs"),
          C("iso13485", "8.4 analysis of data"),
          C("iso17025", "7.6 evaluation of measurement uncertainty"),
          C("iso27001", "9.1 monitoring, measurement, analysis & evaluation"),
          C("iatf16949", "9.1.1.3 application of statistical concepts"),
          C("as9100", "9.1.3"),
          C("sqf", "2.5.5 product sampling, inspection & analysis"),
          C("brcgs", "3.11 management of incidents & product recall"),
          C("fssc22000", "ISO 22000 §9.1"),
        ],
      },
    ],
  },
  {
    group: "Business Continuity, Security & Compliance",
    features: [
      {
        id: "infosec",
        title: "Information security · JWT auth, encrypted transport, object storage",
        summary: "Authentication via JWT, HTTPS-only transport, signed audit log of admin actions, scoped data per organization (multi-tenant isolation by org_id).",
        citations: [
          C("iso27001", "A.5.10 acceptable use", "A.5.15-18 access control", "A.5.31 legal/contractual", "A.8.24 cryptography", "A.8.28 secure coding"),
          C("iso22301", "8.4 business continuity strategies"),
          C("iso9001", "7.1.3 infrastructure", "7.1.4 environment"),
          C("cfr_part_11", "§11.10(c) protection of records", "§11.10(d) limit access"),
        ],
      },
      {
        id: "lawful",
        title: "Anti-bribery · social accountability hooks (configurable)",
        summary: "Destination / dispatch records create a transparent trail of who sent what to whom; admin role-change audit; controller-only billing controls.",
        citations: [
          C("iso37001", "5.2 anti-bribery policy", "8.2 due diligence", "8.7 raising concerns"),
          C("sa8000", "1 child labor", "2 forced labor", "4 freedom of association", "9 management system"),
        ],
      },
    ],
  },
];

// Convenience map for fast lookup
export const STANDARD_BY_KEY = Object.fromEntries(STANDARDS.map((s) => [s.key, s]));
